import styled, { css } from 'styled-components'

import { mediaQueries } from '@fourthwall/components'

import { DASHBOARD_STYLED_CONSTS } from './consts'

export const Header = styled.header<{ isScrolled: boolean }>`
  height: ${DASHBOARD_STYLED_CONSTS.HEADER_HEIGHT}px;
  padding: 0 24px;
  background: var(--bg-primary);

  border-bottom: 1px solid transparent;
  transition: border-color var(--motion-duration-100) var(--motion-ease-in-out);

  ${({ isScrolled }) =>
    isScrolled &&
    css`
      border-color: var(--border-transparent);
    `}

  position: sticky;
  top: 0;
  z-index: 7;

  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: var(--space-300);
`

export const HeaderSide = styled.div<{ $alignRight?: boolean }>`
  display: flex;
  align-items: center;
  ${(props) => props.$alignRight && 'justify-content: flex-end;'}
`

export const ToolbarCenter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  //max-width: 600px;

  /* When empty, this column collapses but still exists in the grid */
  &:empty {
    max-width: 0;
  }
`

export const ShopQuickActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-200);
`

export const HeaderLeftContent = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-300);
`

export const ToolbarRightContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0;

  ${mediaQueries.tablet} {
    gap: var(--space-250);
  }
`

export const ToolbarSpacer = styled.div`
  width: 8px;

  ${mediaQueries.tablet} {
    width: 0;
    display: none;
  }
`

export const ActionIconsWrapper = styled.div`
  display: flex;
  ${mediaQueries.tablet} {
    gap: var(--space-100);
  }
`

export const Main = styled.div`
  display: flex;

  ${mediaQueries.laptop} {
    min-height: calc(100vh - ${DASHBOARD_STYLED_CONSTS.HEADER_HEIGHT}px);
  }
`

export const Sidebar = styled.aside`
  flex: 0 0 ${DASHBOARD_STYLED_CONSTS.SIDEBAR_WIDTH}px;
`

export const SidebarInner = styled.div`
  padding: var(--space-300);

  position: sticky;
  top: ${DASHBOARD_STYLED_CONSTS.HEADER_HEIGHT}px;
`

export const Body = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`

export const ContentWithChatWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  min-width: 0;
  gap: var(--space-400);
  min-height: 0;
`

export const Content = styled.div<{ $isWebView: boolean; $chatbotOpen: boolean }>`
  flex: 1;

  width: 100%;

  padding: ${({ $isWebView }) =>
    `${
      $isWebView
        ? `${DASHBOARD_STYLED_CONSTS.CONTENT_PADDING_HORIZONTAL}px`
        : `${DASHBOARD_STYLED_CONSTS.CONTENT_PADDING_TOP}px ${DASHBOARD_STYLED_CONSTS.CONTENT_PADDING_HORIZONTAL}px
    ${DASHBOARD_STYLED_CONSTS.CONTENT_PADDING_BOTTOM}px;`
    }`};

  margin-left: auto;
  margin-right: auto;

  ${mediaQueries.laptop} {
    padding: ${DASHBOARD_STYLED_CONSTS.CONTENT_PADDING_HORIZONTAL_LAPTOP}px;
    // This makes some room at the bottom of the page to avoid widgets floating over pagination.
    padding-bottom: ${DASHBOARD_STYLED_CONSTS.CONTENT_PADDING_HORIZONTAL_LAPTOP + 24}px;

    ${({ $chatbotOpen }) =>
      $chatbotOpen &&
      css`
        overflow-y: auto;
        overflow-x: hidden;

        // Dynamic right margin for chatbot - pushes entire content (including scrollbar) to the left
        margin-right: ${DASHBOARD_STYLED_CONSTS.CHATBOT_PANEL_WIDTH}px;

        // Custom scrollbar styling
        // Firefox
        scrollbar-width: thin;
        scrollbar-color: var(--color-gray-400) transparent;

        // Webkit (Chrome, Safari, Edge, Opera)
        &::-webkit-scrollbar {
          width: var(--space-200);
        }

        &::-webkit-scrollbar-track {
          background: transparent;
        }

        &::-webkit-scrollbar-thumb {
          background: var(--color-gray-400);
          border-radius: var(--space-100);
        }

        &::-webkit-scrollbar-thumb:hover {
          background: var(--color-gray-500);
        }
      `}
  }
`

export const ChatbotPortalWrapper = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: ${DASHBOARD_STYLED_CONSTS.HEADER_HEIGHT}px;
  right: 0;
  bottom: 0;
  width: ${({ $isOpen }) => ($isOpen ? `${DASHBOARD_STYLED_CONSTS.CHATBOT_PANEL_WIDTH}px` : '0')};

  padding: var(--space-300) var(--space-300) var(--space-300) var(--space-100);
  overflow: hidden;
  transition: width var(--motion-duration-100) var(--motion-ease-in-out);

  z-index: 10;

  display: none;

  ${mediaQueries.tablet} {
    display: block;
  }
`

export const OmnisearchPortalWrapper = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${({ $isOpen }) => ($isOpen ? '9999' : '-1')};
  opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  transition: opacity 0.2s ease-in-out;
`

export const Navbar = styled.div`
  padding: 0 16px;
  position: fixed;
  top: 0;
  z-index: 2;
  width: 100%;
  background: var(--bg-primary);
  height: ${DASHBOARD_STYLED_CONSTS.NAVBAR_HEIGHT}px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${mediaQueries.laptop} {
    transform: initial;
    position: static;
    padding: 24px 24px 12px 24px;
  }
`

export const BurgerButton = styled.button`
  position: relative;
  padding: 10px;
  left: -10px;
`

export const NavbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

export const BannersWrapper = styled.div`
  margin: ${DASHBOARD_STYLED_CONSTS.NAVBAR_HEIGHT}px 0 -${DASHBOARD_STYLED_CONSTS.NAVBAR_HEIGHT}px;

  ${mediaQueries.laptop} {
    margin: 0;
  }
`

export const HeaderMobile = styled.div`
  & > * + * {
    margin-top: var(--space-300);
  }
`

export const HeaderTopWrapperMobile = styled.div`
  & > * + * {
    margin-top: var(--space-150);
  }
`

export const HeaderTopMobile = styled.div`
  display: flex;
  gap: var(--space-200);
  justify-content: space-between;
`
